/** @module Camunda/TaskViewHeader */
define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"underscore",
	"csi",
	"csi/def",
	"csi/html",
	"csi/html/Widget",
	"csi/locale",
	"csi/task/util",
	"csi/view/ToolbarInfo",
	"csi/widget/icon/Util",
	"csi/xml",
	"Camunda",
	"./ToolbarCompleteTask",
	"dojo/text!./ViewHeader.html"
], function(declare, Memory, _, csi, def, html, Widget, locale, TaskUtil, ToolbarInfo, iconUtil, xml, Camunda, ToolbarCompleteTask, template) {

	const _ToolbarInfo = declare(ToolbarInfo, {
		/**
		 * @override
		 * Show plugin if there are no selected tasks
		 */
		canShowPlugin: function() {
			return !this.getView().hasSelectedTasks();
		}
	});

	return declare(Widget, {
		templateString: template,

		/** @property {boolean} _forceDisabled - true if in the process of switching between tasks or batch group so info plugin will not flash */
		_forceDisabled: false,

		/**
		 * Return true if group is selected and there is at least one selected task; also make sure
		 * that in the process of completing a task that it acts as though there is still a selected task
		 * @return {boolean}
		 */
		hasSelectedTasks: function() {
			const filmstrip = this.viewParent;
			return !filmstrip.isBatchGroupSelected() || filmstrip.hasSelectedTasks();
		},

		/**
		 * Extension point to react to selected task(s) being claimed
		 */
		onClaimTasks: function() { },

		/**
		 * Extension point to react to selected task(s) being completed
		 */
		onCompleteTasks: function() { },

		/**
		 * @override
		 * Specify toolbar plugins for task actions
		 */
		onViewCreate: function() {
			const cache = this.cacheView;
			const defView = this.defView;
			const plugins = this.plugins = [];
			const task = this.getViewNode();
			const definition = cache.get(task.TaskDefinition);
			const actionGroup = cache.get(definition.ActionGroup);
			const actions = cache.getNodes(actionGroup.Actions);
			const sortedActions = csi.sort(actions, "SortOrder");
			const canShowCompleteAction = this._canShowActionCompleteTask.bind(this);

			function addCompleteAction(action, params) {
				const actionId = action ? action.$Name : "";

				plugins.push(defView._addViewAction("CamundaAction_" + actionId, {
					classControllerParams: {bShowWait: true},
					classToolbarPlugin: ToolbarCompleteTask,
					classToolbarPluginParams: params,
					m_class: html.CSS_BUTTON_SECONDARY + (action ? " csi-drop-down-button-hide-arrow" : ""),
					m_name: action ? locale.getNodeName(action) : locale.resource("CamundaTaskComplete"),
					m_accessAction: "_accessActionTasks",
					m_onAction: "_onActionCompleteTasks",
					m_postAction: "_postActionCompleteTasks"
				}));
			}

			if (actions.length) {
				plugins.push(defView._addViewAction("CamundaBatchCompleteInfo", {
					classToolbarPlugin: _ToolbarInfo
				}));
			}

			plugins.push(defView._addViewAction("CamundaActionClaim", {
				classControllerParams: {bShowWait: true},
				classToolbarPluginParams: {
					canShowPlugin: this._canShowActionClaimTask.bind(this)
				},
				m_typeControl: def.ACTION_BUTTON,
				m_class: html.CSS_BUTTON_SECONDARY,
				m_accessAction: "_accessActionTasks",
				m_onAction: "_onActionClaimTasks",
				m_postAction: "_postActionClaimTasks"
			}));

			if (sortedActions.length > 2) {
				// add single drop-down button
				const data = _.map(sortedActions, function(action) {
					return {
						id: action.$Name,
						name: locale.getNodeName(action),
						label: iconUtil.getTaskActionIconHtml(action.Icon, action.Color, locale.getNodeName(action))
					};
				});

				addCompleteAction(null, {
					canShowPlugin: canShowCompleteAction,
					taskActionStore: new Memory({data: data})
				});
			} else {
				// add individual buttons for 1 or 2 actions
				_.each(sortedActions.reverse(), function(action) {
					addCompleteAction(action, {
						canShowPlugin: canShowCompleteAction,
						iconHtml: iconUtil.getTaskActionIconHtml(action.Icon, action.Color),
						taskAction: action.$Name
					});
				});
			}
		},

		/**
		 * Return true only if there is a selected task and the user has access to the instance;
		 * in the case of batched view though, let server handle the logic to determine if the tasks can be acted upon
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionTasks: function(ctx, action) {
			return this.viewParent.hasSelectedTasks() && !!ctx.getChildNode("C8Instance");
		},

		/**
		 * Return true if user can claim the selected task; if in batch mode, then show only if in Available Tasks view
		 * @return {boolean} 
		 */
		_canShowActionClaimTask: function() {
			const filmstrip = this.viewParent;
			const task = filmstrip.getFilmstripSelectedNode();

			if (filmstrip.isBatchGroupSelected()) {
				// in the case of batch mode, check if all these tasks are mine, if so, show task action instead, otherwise, show claim action
				const allMyself = filmstrip.isAllTasksMyselfInBatchMode();
				return !allMyself;
			}

			// in the case of normal mode
			return task && Camunda.canClaimTask(task);
		},

		/**
		 * Return true if user can complete the selected task
		 * @return {boolean} 
		 */
		_canShowActionCompleteTask: function() {
			const filmstrip = this.viewParent;

			if (filmstrip.isBatchGroupSelected()) {
				// in the case of batch mode, if all of the tasks are mine, show task actions
				return filmstrip.isAllTasksMyselfInBatchMode();
			}

			const selectedNode = filmstrip.getFilmstripSelectedNode();
			return selectedNode && TaskUtil.canCompleteTask(selectedNode);
		},

		/**
		 * @override
		 * Prevent enabling the view when forced to be disabled
		 */
		_disableView: function(disabled) {
			if (!this._forceDisabled || disabled) {
				this.inherited(arguments);
			}
		},

		/**
		 * @override
		 * Toolbar is used to render completion actions
		 */
		_hasToolbar: function() {
			return true;
		},

		/**
		 * Complete selected task, or tasks if batched
		 * @param {Controller} ac
		 */
		_onActionCompleteTasks: function(ac) {
			const filmstrip = this.viewParent;
			const taskUrls = filmstrip.get("selectedTasks");
			const values = ac.actionValue;
			const writer = ac.m_writer;

			if (filmstrip.isBatchGroupSelected()) {
				writer.addParameter("FaultTolerant", "true");
			}

			_.each(taskUrls, function(url) {
				writer.callMethod("Camunda", "CompleteTask")
					.addParameter(xml.PROP_URL, xml.ATTR_REF, url)
					.addParameter("UserResponse", xml.ATTR_STRING, values.action)
					.addParameter("Comments", xml.ATTR_STRING, values.comment);
			});
		},

		/**
		 * Claim selected task, or tasks if batched
		 * @param {Controller} ac
		 */
		_onActionClaimTasks: function(ac) {
			const taskUrls = this.viewParent.get("selectedTasks");
			const writer = ac.m_writer;

			_.each(taskUrls, function(url) {
				writer.callMethod("Camunda", "ClaimTask").addParameter(xml.PROP_URL, xml.ATTR_REF, url);
			});
		},

		/**
		 * Handle claim of the selected task(s)
		 * @param {Controller} ac
		 */
		_postActionClaimTasks: function(ac) {
			// force view to disabled since it will refresh after claiming
			this._forceDisabled = true;
			this.onClaimTasks();
		},

		/**
		 * Handle completion of the selected task(s)
		 * @param {Controller} ac
		 */
		_postActionCompleteTasks: function(ac) {
			// force view to disabled since it will refresh after completing
			this._forceDisabled = true;

			const response = ac.m_response;
			const failedUrls = [];

			if (response.someFailed()) {
				const errorReports = response.getErrorReports();
				const errorMessages = [];
				const instanceUrls = {};

				errorReports.forEach(function(er) {
					const url = er.Param.URL;

					if (!instanceUrls[url]) {
						instanceUrls[url] = true;
						errorMessages.push(er.Param.$ + ": " + locale.parameterResource(er.Error, er.Param));
					}

					failedUrls.push(er.Operation.Call.Parameter[0].Value);
				});

				ac.displayErrorDialog({
					message: errorMessages.join(";\n"),
					onResponse: function() { }
				});
			}

			this.onCompleteTasks(failedUrls);
		}
	});
});
